import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { withLang } from './LanguageRouter';

const CookieConsent = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
        // Trigger animation after component mounts
        requestAnimationFrame(() => setIsAnimating(true));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
  };

  const declineCookies = () => {
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[9998] p-4 transition-all duration-500 ease-out"
      style={{
        transform: isAnimating ? 'translateY(0)' : 'translateY(100%)',
        opacity: isAnimating ? 1 : 0
      }}
    >
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="flex items-start gap-4">
            {/* Cookie Icon */}
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Cookie className="w-6 h-6 text-blue-600" />
            </div>
            
            {/* Content */}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('cookies.bannerTitle') || 'We use cookies'}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {t('cookies.bannerDescription') || 'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of cookies.'}
              </p>
              
              {/* Links */}
              <div className="flex flex-wrap gap-4 text-sm mb-4">
                <Link 
                  to={withLang(currentLanguage, '/cookie-policy')}
                  className="text-blue-600 hover:text-blue-800 underline transition-colors"
                >
                  {t('footer.cookiePolicy') || 'Cookie Policy'}
                </Link>
                <Link 
                  to={withLang(currentLanguage, '/privacy')}
                  className="text-blue-600 hover:text-blue-800 underline transition-colors"
                >
                  {t('footer.privacyPolicy') || 'Privacy Policy'}
                </Link>
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={acceptCookies}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm"
                >
                  {t('cookies.acceptAll') || 'Accept All'}
                </button>
                <button
                  onClick={declineCookies}
                  className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200"
                >
                  {t('cookies.declineOptional') || 'Decline Optional'}
                </button>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={declineCookies}
              className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close cookie banner"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
